/**
 * Extrai e valida emails de planilhas CSV/XLSX.
 *
 * Regras:
 * - Planilha deve conter coluna de email (variações: email, E-mail, e-mail, EMAIL, E-MAIL, E_MAIL, etc.)
 * - Limite de 100 emails por vez
 * - Emails devem ter formato válido
 * - Emails devem pertencer ao domínio da empresa (allowedDomains)
 */
import * as XLSX from "xlsx";

const MAX_EMAILS = 100;

/** Aceita coluna com nome email em qualquer variação: email, E-mail, e-mail, EMAIL, E-MAIL, E_MAIL, etc. */
const EMAIL_COLUMN_PATTERN = /^(e[-_]?\s*mail|emails?|mail)$/i;

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function isValidEmailFormat(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  if (!EMAIL_REGEX.test(trimmed)) return false;
  if (trimmed.includes("..")) return false;
  if (trimmed.startsWith(".") || trimmed.endsWith(".")) return false;
  const [local, domain] = trimmed.split("@");
  if (local.length > 64 || domain.length > 255) return false;
  return true;
}

function isEmailInAllowedDomains(
  email: string,
  allowedDomains: string[],
): boolean {
  if (!allowedDomains || allowedDomains.length === 0) return true;
  const emailDomain = email.trim().toLowerCase().split("@")[1];
  if (!emailDomain) return false;
  return allowedDomains.some((d) => d.trim().toLowerCase() === emailDomain);
}

export type ValidationResult =
  | { valid: true; email: string }
  | { valid: false; reason: InvalidEmail["reason"] };

/**
 * Corrige um email para usar o domínio válido da empresa.
 * - fulano@dominioerrada.com.br → fulano@dominiocorreto.com.br
 * - fulanoteste (sem @) → fulanoteste@dominiocorreto.com.br
 */
export function autocorrectEmailToDomain(
  raw: string,
  allowedDomains: string[],
): string {
  if (!allowedDomains?.length) return raw.trim().toLowerCase();
  const domain = allowedDomains[0].trim().toLowerCase();
  const trimmed = raw.trim().toLowerCase();
  if (trimmed.includes("@")) {
    const local = trimmed.split("@")[0] ?? trimmed;
    return `${local}@${domain}`;
  }
  return `${trimmed}@${domain}`;
}

/** Valida um único email. Usado para revalidar após edição na modal. */
export function validateSingleEmail(
  raw: string,
  allowedDomains: string[],
  existingValidEmails: string[],
): ValidationResult {
  const email = raw.trim().toLowerCase();
  if (!email) return { valid: false, reason: "formato_invalido" };
  if (!isValidEmailFormat(email))
    return { valid: false, reason: "formato_invalido" };
  if (!isEmailInAllowedDomains(email, allowedDomains))
    return { valid: false, reason: "dominio_invalido" };
  if (existingValidEmails.includes(email))
    return { valid: false, reason: "duplicado" };
  return { valid: true, email };
}

function findEmailColumn(headers: string[]): number {
  for (let i = 0; i < headers.length; i++) {
    const h = (headers[i] ?? "").trim().replace(/\s+/g, " ");
    if (EMAIL_COLUMN_PATTERN.test(h)) return i;
  }
  return -1;
}

export interface InvalidEmail {
  email: string;
  reason: "formato_invalido" | "dominio_invalido" | "duplicado";
}

export interface ParseEmailsResult {
  validEmails: string[];
  invalidEmails: InvalidEmail[];
  hasEmailColumn: boolean;
  totalRows: number;
}

export function parseEmailsFromSpreadsheet(
  file: File,
  allowedDomains: string[],
): Promise<ParseEmailsResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error("Não foi possível ler o arquivo."));
          return;
        }

        const workbook = XLSX.read(data, {
          type: "binary",
          cellDates: true,
          cellNF: false,
          cellText: false,
        });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<string[]>(firstSheet, {
          header: 1,
          defval: "",
        }) as string[][];

        if (!rows || rows.length < 2) {
          resolve({
            validEmails: [],
            invalidEmails: [],
            hasEmailColumn: false,
            totalRows: rows?.length ?? 0,
          });
          return;
        }

        const headers = rows[0].map((h) => String(h ?? "").trim());
        const emailColIdx = findEmailColumn(headers);

        if (emailColIdx < 0) {
          resolve({
            validEmails: [],
            invalidEmails: [],
            hasEmailColumn: false,
            totalRows: rows.length - 1,
          });
          return;
        }

        const rawEmails = new Set<string>();
        const validEmails: string[] = [];
        const invalidEmails: InvalidEmail[] = [];

        for (
          let i = 1;
          i < rows.length &&
          validEmails.length + invalidEmails.length < MAX_EMAILS;
          i++
        ) {
          const row = rows[i];
          const raw = String(row?.[emailColIdx] ?? "").trim();
          if (!raw) continue;

          const email = raw.toLowerCase();

          if (rawEmails.has(email)) {
            invalidEmails.push({ email, reason: "duplicado" });
            continue;
          }
          rawEmails.add(email);

          if (!isValidEmailFormat(email)) {
            invalidEmails.push({ email, reason: "formato_invalido" });
            continue;
          }

          if (!isEmailInAllowedDomains(email, allowedDomains)) {
            invalidEmails.push({ email, reason: "dominio_invalido" });
            continue;
          }

          validEmails.push(email);
        }

        resolve({
          validEmails,
          invalidEmails,
          hasEmailColumn: true,
          totalRows: rows.length - 1,
        });
      } catch (err) {
        reject(
          err instanceof Error ? err : new Error("Erro ao processar planilha."),
        );
      }
    };
    reader.onerror = () => reject(new Error("Erro ao ler o arquivo."));
    reader.readAsBinaryString(file);
  });
}
